import { InputType } from 'config'
import { setLoanNumber } from 'utils/setLoanNumber'

const dayOfMonthOptions = Array.from({ length: 31 }, (_, i) => String(i + 1))

const withHistory = (input: InputType): InputType => ({ ...input, history: true })

export const defaultInputs = (): Record<string, InputType> => {
  const loanNumber = setLoanNumber()
  return {
    achFlag: withHistory({
      inputType: 'select',
      options: ['Y', 'N'],
      title: 'ACH Flag',
      hasDefaultOption: true,
    }),
    accountType: withHistory({
      inputType: 'select',
      options: ['Checking', 'Savings'],
      title: 'ACH Account Type',
      hasDefaultOption: true,
      tooltip:
        'Whether the borrower\'s account is a checking or savings account. This picks the NACHA ' +
        'transaction code on the entry, so a savings account drafted as checking is returned by ' +
        'the receiving bank.',
    }),
    accountNo: withHistory({
      inputType: 'text',
      type: 'bankAccountNo',
      title: 'Account #',
      tooltip: 'The borrower\'s account number at the institution below. Digits only, up to 17.',
    }),
    accountRoutingNo: withHistory({
      inputType: 'text',
      type: 'routingNo',
      title: 'Account Routing #',
      tooltip:
        'The 9-digit ABA routing number of the borrower\'s bank. Entering it fills in the ' +
        'institution name below from the routing directory.',
    }),
    accountFinancialInsName: withHistory({
      inputType: 'text',
      title: 'Account Financial Institution Name',
      tooltip:
        'Filled in from the routing number when the directory knows it. Type over it freely - a ' +
        'name you enter yourself is never replaced by a later lookup.',
    }),
    accountName: withHistory({
      inputType: 'text',
      title: 'Name of Account',
      tooltip: 'The name the account is held under, as it appears at the bank. Sent on every entry.',
    }),
    depositAccount: {
      inputType: 'section',
      title: 'Deposit Account - optional, for money paid out to the borrower',
      span: 'full',
    },
    depositAccountType: withHistory({
      inputType: 'select',
      options: ['Checking', 'Savings'],
      title: 'Deposit Account Type',
      hasDefaultOption: true,
      tooltip:
        'Whether the deposit account is checking or savings. Picks the NACHA transaction code on ' +
        'the credit, so a savings account sent as checking is returned by the receiving bank.',
    }),
    depositAccountNo: withHistory({
      inputType: 'text',
      type: 'bankAccountNo',
      title: 'Deposit Account #',
      tooltip:
        'Leave this whole section blank and draws and refunds go to the account above, the same ' +
        'one payments are drafted from. Fill it in when the borrower takes deposits somewhere ' +
        'else - a construction account, say - and money paid OUT to them goes here instead. ' +
        'Drafts never use it.',
    }),
    depositAccountRoutingNo: withHistory({
      inputType: 'text',
      type: 'routingNo',
      title: 'Deposit Account Routing #',
      tooltip:
        'The 9-digit ABA routing number of the deposit bank. Entering it fills in the ' +
        'institution name below from the routing directory.',
    }),
    depositAccountFinancialInsName: withHistory({
      inputType: 'text',
      title: 'Deposit Account Financial Institution Name',
      tooltip:
        'Filled in from the routing number when the directory knows it. Type over it freely - a ' +
        'name you enter yourself is never replaced by a later lookup.',
    }),
    depositAccountName: withHistory({
      inputType: 'text',
      title: 'Name of Deposit Account',
      tooltip: 'The name the deposit account is held under, as it appears at the bank.',
    }),
    draft: {
      inputType: 'section',
      title: 'Payment Draft',
      span: 'full',
    },
    draftAmountMode: withHistory({
      inputType: 'select',
      title: 'ACH Draft Amount Mode',
      options: {
        piti: 'PITI Only',
        pitiPlusCharges: 'PITI + All Charges Due',
        override: 'Override Amount',
      },
      hasDefaultOption: false,
      tooltip:
        'What the draft collects each run. PITI Only is the Servicing Information Total Payment ' +
        '(P&I + Escrow Monthly, plus any servicing fee and Other Payments). PITI + All Charges Due ' +
        'adds every non-deferred unpaid borrower charge. Override Amount lets you type a figure. ' +
        'Deferred charges (parked to payoff) are never included.',
    }),
    draftAmount: withHistory({
      inputType: 'text',
      type: 'thousandSep',
      prefix: '$',
      title: 'ACH Draft Amount',
      readOnly: true,
      tooltip:
        'What will be drafted on the next run. Read-only unless Override Amount is selected. ' +
        'With PITI + All Charges Due, this is PITI plus open borrower charges (deferred charges ' +
        'excluded).',
    }),
    draftDayOfMonth: withHistory({
      inputType: 'select',
      options: dayOfMonthOptions,
      hasDefaultOption: true,
      title: 'Draft Day of Month',
      tooltip:
        'The day of the month this borrower is drafted on. Clamped to shorter months, so the ' +
        '31st drafts on the 28th in February.',
    }),
    secCode: withHistory({
      inputType: 'select',
      options: ['PPD', 'CCD', 'WEB', 'TEL'],
      hasDefaultOption: true,
      title: 'SEC Code',
      tooltip:
        'The NACHA Standard Entry Class that says how the borrower authorized the draft, and it ' +
        'has to match how you actually got the authorization. ' +
        'PPD (Prearranged Payment and Deposit): a consumer who signed a written authorization - ' +
        'the usual choice for a servicing autopay. ' +
        'CCD (Corporate Credit or Debit): an account held by a business or entity, not a person. ' +
        'WEB: a consumer who authorized it online. ' +
        'TEL: a consumer who authorized it over the phone. ' +
        'Blank is sent as PPD.',
    }),
    dealyDays: withHistory({
      inputType: 'text',
      type: 'thousandSepNoDecimal',
      title: 'ACH Delay Days',
      tooltip:
        'How many days after the Next Payment Date this borrower is drafted, so a grace period can ' +
        'pass before their account is debited. Due on the 1st with 5 delay days, the loan is ' +
        'picked up by the first ACH run on or after the 6th. Blank or 0 drafts on the Next ' +
        'Payment Date.',
    }),
    nextPaymentDate: withHistory({
      inputType: 'text',
      type: 'date',
      title: 'Next Payment Date',
      tooltip:
        'The date this loan is next drafted. A run only drafts loans whose next draft falls on or ' +
        'before the run date. Defaults to the Next Payment Date on Servicing Information; typing ' +
        'a date here overrides that.',
    }),
    // How often this loan is drafted. A plain field rather than the full recurrence widget: an ACH
    // schedule is a cadence and an end date, and the widget's repeat/interval/weekday machinery was
    // answering a question nobody asked here.
    achFrequency: withHistory({
      inputType: 'select',
      title: 'ACH Frequency',
      options: ['Daily', 'Weekly', 'Biweekly', 'Monthly', 'Bimonthly', 'Quarterly', 'Semi Annualy', 'Annualy'],
      hasDefaultOption: true,
      sort: true,
      tooltip: 'How often this borrower is drafted. Defaults to the loan\'s servicing Payment Frequency.',
    }),
    // Stored inside the recurrence (`data.frequency.endDate`) but rendered here, beside the date
    // it bounds, rather than inside the Frequency widget.
    frequencyEndDate: {
      inputType: 'text',
      type: 'date',
      title: 'ACH End Date',
      required: false,
      tooltip:
        'The last date this loan is drafted. Once it passes, the loan is taken off ACH ' +
        'automatically (ACH Flag flips to N) and is skipped by every later run. Blank = no end.',
    },
    authorization: {
      inputType: 'section',
      title: 'ACH Authorization',
      span: 'full',
    },
    authorizationDate: withHistory({
      inputType: 'text',
      type: 'date',
      title: 'Authorization Date',
    }),
    authorizationForm: withHistory({
      inputType: 'FileTable',
      title: 'ACH Authorization Form',
      value: [],
      span: 'full',
      filePath: `loan/${loanNumber}/ach`,
      required: false,
    }),
  }
}
